import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import { STAGE_ROUTE } from '#/entities/shipment'
import type { ShipmentStage } from '#/entities/shipment'
import type { ShipmentStatus } from '#/shared/api/generated/shipment/models'
import { getGetShipmentApiV1ShipmentsShipmentIdGetQueryOptions } from '#/shared/api/generated/shipment/theCargoShipment'
import { useDetailSidebar } from '#/shared/ui/detail-sidebar'

import { ShipmentSidebarHeader } from '../ui/shipment-sidebar-header'
import { ShipmentSidebarPanel } from '../ui/shipment-sidebar-panel'

/**
 * Imperatively open the shipment detail sidebar for a given stage + id, writing
 * straight to the global sidebar store instead of the URL `preview` param. The
 * param is owned per-page by `useShipmentSidebar` and scoped to that page's
 * stage, so driving it from a global surface (the phone panel floats over any
 * route) would collide on lead/quote pages. This opener has no such coupling:
 * the preview is an ephemeral action, not deep-linkable page state. Expand still
 * navigates to the full detail page.
 */
export function useOpenShipmentPreview() {
  const { open } = useDetailSidebar()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useCallback(
    (
      stage: ShipmentStage,
      shipmentId: string,
      optimisticStatus?: ShipmentStatus,
    ) => {
      const route = STAGE_ROUTE[stage]
      void queryClient.prefetchQuery(
        getGetShipmentApiV1ShipmentsShipmentIdGetQueryOptions(shipmentId),
      )
      open({
        id: shipmentId,
        header: <ShipmentSidebarHeader shipmentId={shipmentId} />,
        content: (
          <ShipmentSidebarPanel
            stage={stage}
            shipmentId={shipmentId}
            optimisticStatus={optimisticStatus}
          />
        ),
        onExpand: () =>
          void navigate({
            to: route.to as never,
            params: { [route.paramKey]: shipmentId } as never,
            search: ((prev: Record<string, unknown>) => prev) as never,
          }),
      })
    },
    [open, navigate, queryClient],
  )
}
